// Ming Yin Kiosk - Enrollment Form Types

// ============ BASIC INFO ============

export interface BodyMetrics {
  heightValue?: number;
  heightUnit: 'meters' | 'feet';
  weightValue?: number;
  weightUnit: 'kg' | 'lbs';
}

export interface SymptomOnset {
  year?: number;
  month?: number;
}

// ============ MEDICAL HISTORY ============

export type MedicalHistoryType = 'surgery' | 'injury' | 'accident' | 'implant';

export interface MedicalHistoryItem {
  type: MedicalHistoryType;
  hasHistory: boolean;
  details?: string;
}

export interface ImagingHistory {
  xray: boolean;
  ctScan: boolean;
  mri: boolean;
  electrotherapy: boolean;
  magneticTherapy: boolean;
  previousTreatment?: string;
  treatmentEffectiveness?: string;
}

// Medical conditions list (used for checkbox options)
export const MEDICAL_CONDITIONS = [
  'highBloodPressure',
  'diabetes',
  'heartDisease',
  'thyroidDisease',
  'liverGallbladderStomach',
  'cancerTumorSurgery',
  'breastConditions',
  'allergies',
  'insomnia',
  'obesity',
  'thalassemia',
  'pregnancyPostpartumMenstrual',
  'anxietyEmotional',
  'kidneyProblems',
  'reproductivePelvic',
] as const;

export type MedicalCondition = typeof MEDICAL_CONDITIONS[number];

// ============ PAIN ASSESSMENT ============

export type PainSide = 'left' | 'right' | 'both' | 'center';

export interface PainLocation {
  area: string;
  side?: PainSide;
}

// Pain location options
export const PAIN_AREAS = [
  { key: 'neck', hasSides: false },
  { key: 'shoulder', hasSides: true },
  { key: 'upperBack', hasSides: false },
  { key: 'lowerBack', hasSides: false },
  { key: 'lumbar', hasSides: false },
  { key: 'hips', hasSides: true },
  { key: 'knees', hasSides: true },
  { key: 'armsElbowsWrists', hasSides: true },
  { key: 'legsSciatica', hasSides: true },
  { key: 'other', hasSides: false },
] as const;

export type PainArea = typeof PAIN_AREAS[number]['key'];

// Pain types
export const PAIN_TYPES = ['dull', 'sharp', 'radiating', 'numbness', 'tingling'] as const;
export type PainType = typeof PAIN_TYPES[number];

// Pain triggers
export const PAIN_TRIGGERS = ['movement', 'lifting', 'prolongedStanding'] as const;
export type PainTrigger = typeof PAIN_TRIGGERS[number];

export type PainFrequency = 'constant' | 'intermittent';

export interface PainCharacteristics {
  types: PainType[];
  triggers: PainTrigger[];
  severity?: number; // 0-10
  frequency?: PainFrequency;
}

// ============ SYMPTOM CHECKLISTS ============

export type SymptomCategory = 'neck' | 'shoulder' | 'spine' | 'lumbar' | 'knee' | 'joints' | 'other';

export interface SymptomChecklist {
  category: SymptomCategory;
  symptoms: string[];
}

// Neck symptoms
export const NECK_SYMPTOMS = [
  'dizzinessHeadaches',
  'numbnessUpperLimbs',
  'eyeStrainBlurredVision',
  'poorBalanceWalking',
  'tinnitusHearingReduction',
] as const;

// Shoulder symptoms
export const SHOULDER_SYMPTOMS = [
  'limitedBackwardMovement',
  'painLiftingArms',
  'tendernessStiffness',
] as const;

// Spine/posture symptoms
export const SPINE_SYMPTOMS = [
  'chestTightness',
  'legLengthDifference',
  'scoliosisAwareness',
] as const;

// Lumbar symptoms
export const LUMBAR_SYMPTOMS = [
  'radiatingLegPain',
  'sciatica',
  'difficultyBendingStanding',
  'herniationSymptoms',
  'backSpasms',
] as const;

// Knee symptoms
export const KNEE_SYMPTOMS = [
  'clickingSounds',
  'difficultyClimbingStairs',
  'swelling',
  'weatherSensitivePain',
] as const;

// Joint symptoms
export const JOINT_SYMPTOMS = [
  'stiffness',
  'inabilityStraightenLimbs',
  'fearOfCold',
] as const;

// Other symptoms
export const OTHER_SYMPTOMS = [
  'fatigue',
  'weakness',
  'sleepDisturbance',
] as const;

// ============ LIFESTYLE ============

export type SleepQuality = 'poor' | 'fair' | 'good' | 'excellent';
export type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active';

export const WORK_POSTURE_OPTIONS = ['deskWork', 'lifting', 'repetitiveTasks'] as const;
export type WorkPosture = typeof WORK_POSTURE_OPTIONS[number];

export interface LifestyleFactors {
  smoking: boolean;
  alcohol: boolean;
  workPosture: WorkPosture[];
  stressLevel?: number; // 0-10
  sleepQuality?: SleepQuality;
  activityLevel?: ActivityLevel;
  prolongedSitting: boolean;
  prolongedStanding: boolean;
}

// ============ WOMEN'S HEALTH ============

export type MenstrualRegularity = 'regular' | 'irregular' | 'none';

export interface WomensHealth {
  isPregnant: boolean;
  isPostpartum: boolean;
  menstrualRegularity?: MenstrualRegularity;
  menstrualPain: boolean;
  gynecologicalConditions?: string;
}

// ============ MEDICATIONS & ALLERGIES ============

export interface MedicationsAndAllergies {
  currentMedications?: string;
  supplements?: string;
  medicineAllergies?: string;
  foodAllergies?: string;
  environmentalAllergies?: string;
}

// ============ TREATMENT PREFERENCES ============

export const THERAPY_OPTIONS = [
  'massage',
  'acupuncture',
  'cupping',
  'manualTherapy',
  'exerciseRehab',
  'physicalTherapy',
  'heatTherapy',
  'electroTherapy',
] as const;

export type TherapyType = typeof THERAPY_OPTIONS[number];

export interface TreatmentPreferences {
  expectedOutcomes?: string;
  preferredTherapies: TherapyType[];
  refusedTreatments?: string;
}

// ============ HEALTH QUESTIONNAIRE ============

export interface HealthQuestionnaire {
  // Basic identifiers
  hkIdPrefix?: string;
  occupation?: string;

  // Body metrics
  bodyMetrics?: BodyMetrics;

  // Chief complaint
  symptomOnset?: SymptomOnset;
  chiefComplaint?: string;
  mainProblemToResolve?: string;

  // Medical conditions
  medicalConditions: string[];

  // Medical history
  medicalHistory: MedicalHistoryItem[];

  // Imaging history
  imagingHistory?: ImagingHistory;

  // Pain assessment
  painLocations: PainLocation[];
  painCharacteristics?: PainCharacteristics;

  // Symptom checklists
  symptomChecklists: SymptomChecklist[];

  // Lifestyle
  lifestyle?: LifestyleFactors;

  // Women's health
  womensHealth?: WomensHealth;

  // Medications and allergies
  medicationsAndAllergies?: MedicationsAndAllergies;

  // Treatment preferences
  treatmentPreferences?: TreatmentPreferences;
}

// ============ FULL ENROLLMENT FORM DATA ============

export interface EnrollmentFormData {
  // Step 1: Personal Info
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: 'Male' | 'Female' | 'Other' | '';
  hkIdPrefix: string;
  occupation: string;

  // Step 2: Contact Info
  phone: string;
  email: string;
  referralSource: string;
  referredBy: string;
  referredByPhone: string;

  // Step 3: Body Metrics
  heightValue: string;
  heightUnit: 'meters' | 'feet';
  weightValue: string;
  weightUnit: 'kg' | 'lbs';

  // Step 4: Chief Complaint
  symptomOnsetYear: string;
  symptomOnsetMonth: string;
  chiefComplaint: string;
  mainProblemToResolve: string;

  // Step 5: Health Conditions
  medicalConditions: string[];
  surgeryHistory: boolean;
  surgeryDetails: string;
  injuryHistory: boolean;
  injuryDetails: string;
  accidentHistory: boolean;
  accidentDetails: string;
  implantHistory: boolean;
  implantDetails: string;
  imagingXray: boolean;
  imagingCt: boolean;
  imagingMri: boolean;
  imagingElectrotherapy: boolean;
  imagingMagneticTherapy: boolean;
  previousTreatment: string;
  treatmentEffectiveness: string;

  // Step 6: Pain Assessment
  painLocations: PainLocation[];
  painOtherDetails: string;
  painTypes: PainType[];
  painTriggers: PainTrigger[];
  painSeverity: number;
  painFrequency: PainFrequency | '';

  // Step 7: Symptom Checklist
  neckSymptoms: string[];
  shoulderSymptoms: string[];
  spineSymptoms: string[];
  lumbarSymptoms: string[];
  kneeSymptoms: string[];
  jointSymptoms: string[];
  otherSymptoms: string[];

  // Step 8: Lifestyle & Treatment
  smoking: boolean;
  alcohol: boolean;
  workPosture: WorkPosture[];
  stressLevel: number;
  sleepQuality: SleepQuality | '';
  activityLevel: ActivityLevel | '';
  prolongedSitting: boolean;
  prolongedStanding: boolean;
  // Women's health (conditional)
  isPregnant: boolean;
  isPostpartum: boolean;
  menstrualRegularity: MenstrualRegularity | '';
  menstrualPain: boolean;
  gynecologicalConditions: string;
  // Medications
  currentMedications: string;
  supplements: string;
  medicineAllergies: string;
  foodAllergies: string;
  environmentalAllergies: string;
  // Treatment preferences
  expectedOutcomes: string;
  preferredTherapies: TherapyType[];
  refusedTreatments: string;

  // Step 9: Emergency Contact (all optional)
  emergencyName: string;
  emergencyPhone: string;
  emergencyRelationship: string;

  // Step 10: Review (read-only)

  // Step 11: Consents
  consentTerms: boolean;
  consentPrivacy: boolean;
  consentHealth: boolean;
  consentMarketing: boolean;

  // Step 12: Signature
  signatureData: string;
  skipSignature: boolean; // Staff enrolling on behalf of client, skip signing
}

// ============ INITIAL FORM DATA ============

export const initialEnrollmentFormData: EnrollmentFormData = {
  // Step 1: Personal Info
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  hkIdPrefix: '',
  occupation: '',

  // Step 2: Contact Info
  phone: '',
  email: '',
  referralSource: 'walk_in',
  referredBy: '',
  referredByPhone: '',

  // Step 3: Body Metrics
  heightValue: '',
  heightUnit: 'meters',
  weightValue: '',
  weightUnit: 'lbs',

  // Step 4: Chief Complaint
  symptomOnsetYear: '',
  symptomOnsetMonth: '',
  chiefComplaint: '',
  mainProblemToResolve: '',

  // Step 5: Health Conditions
  medicalConditions: [],
  surgeryHistory: false,
  surgeryDetails: '',
  injuryHistory: false,
  injuryDetails: '',
  accidentHistory: false,
  accidentDetails: '',
  implantHistory: false,
  implantDetails: '',
  imagingXray: false,
  imagingCt: false,
  imagingMri: false,
  imagingElectrotherapy: false,
  imagingMagneticTherapy: false,
  previousTreatment: '',
  treatmentEffectiveness: '',

  // Step 6: Pain Assessment
  painLocations: [],
  painOtherDetails: '',
  painTypes: [],
  painTriggers: [],
  painSeverity: 0,
  painFrequency: '',

  // Step 7: Symptom Checklist
  neckSymptoms: [],
  shoulderSymptoms: [],
  spineSymptoms: [],
  lumbarSymptoms: [],
  kneeSymptoms: [],
  jointSymptoms: [],
  otherSymptoms: [],

  // Step 8: Lifestyle & Treatment
  smoking: false,
  alcohol: false,
  workPosture: [],
  stressLevel: 5,
  sleepQuality: '',
  activityLevel: '',
  prolongedSitting: false,
  prolongedStanding: false,
  isPregnant: false,
  isPostpartum: false,
  menstrualRegularity: '',
  menstrualPain: false,
  gynecologicalConditions: '',
  currentMedications: '',
  supplements: '',
  medicineAllergies: '',
  foodAllergies: '',
  environmentalAllergies: '',
  expectedOutcomes: '',
  preferredTherapies: [],
  refusedTreatments: '',

  // Step 9: Emergency Contact
  emergencyName: '',
  emergencyPhone: '',
  emergencyRelationship: '',

  // Step 11: Consents
  consentTerms: false,
  consentPrivacy: false,
  consentHealth: false,
  consentMarketing: false,

  // Step 12: Signature
  signatureData: '',
  skipSignature: false, // Staff enrolling on behalf of client, skip signing
};

// ============ VALIDATION ERRORS ============

export type ValidationErrors = Partial<Record<keyof EnrollmentFormData | string, string>>;

// ============ STEP PROPS ============

export interface StepProps {
  formData: EnrollmentFormData;
  errors: ValidationErrors;
  onChange: <K extends keyof EnrollmentFormData>(
    field: K
  ) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: EnrollmentFormData[K] } }) => void;
  onCheckboxChange: <K extends keyof EnrollmentFormData>(
    field: K
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onArrayChange: <K extends keyof EnrollmentFormData>(
    field: K,
    value: EnrollmentFormData[K]
  ) => void;
}
